import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, supabaseConfigured } from '../lib/supabase';
import { setAuthToken, setUnauthorizedHandler } from '../lib/api';

/** admin = JWT app_metadata.role === 'admin' (백엔드 require_admin 과 동일 기준). */
export function deriveIsAdmin(user: User | null): boolean {
  const meta = (user?.app_metadata ?? {}) as { role?: string; roles?: string[] };
  return meta.role === 'admin' || (Array.isArray(meta.roles) && meta.roles.includes('admin'));
}

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  configured: boolean;
  isAdmin: boolean;
  signInWithEmail(email: string, password: string): Promise<{ error: string | null }>;
  signUpWithEmail(email: string, password: string): Promise<{ error: string | null }>;
  signInWithOtp(email: string): Promise<{ error: string | null }>;
  signOut(): Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session ?? null);
      setAuthToken(data.session?.access_token ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setAuthToken(s?.access_token ?? null);
    });
    // When the API gets 401 with a token present, drop the session and
    // redirect to /login. Production-only — in dev we just log so a single
    // misconfigured request doesn't kick the developer out.
    if (import.meta.env.PROD) {
      setUnauthorizedHandler(() => {
        void supabase.auth.signOut();
      });
    } else {
      setUnauthorizedHandler(() => {
        console.warn('[auth] 401 with token present — would sign out in production');
      });
    }
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      setUnauthorizedHandler(null);
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user: session?.user ?? null,
      session,
      loading,
      configured: supabaseConfigured,
      isAdmin: deriveIsAdmin(session?.user ?? null),
      async signInWithEmail(email, password) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error: error?.message ?? null };
      },
      async signUpWithEmail(email, password) {
        const { error } = await supabase.auth.signUp({ email, password });
        return { error: error?.message ?? null };
      },
      async signInWithOtp(email) {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: window.location.origin },
        });
        return { error: error?.message ?? null };
      },
      async signOut() {
        await supabase.auth.signOut();
      },
    }),
    [session, loading],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
